/**
 * Run log (builder Console, WS-3b) — captures the `console.*` output of a manual
 * run in the builder (a JS Object "Prova funzione" test, a query preview) so the
 * Console's "Logs" tab actually shows it (owner feedback: a `console.log` in a JS
 * Object didn't appear). In the builder there is no live page runtime, so the
 * log records the EXPLICIT runs the author triggers — not the portal's full
 * event stream (that's a runtime concern).
 */

export interface LogEntry {
  id: string;
  /** HH:MM:SS local time the run happened. */
  time: string;
  /** What ran (e.g. `Deals.euro(7)` or `Query: recent`). */
  label: string;
  /** Captured `console.*` lines + any error. */
  lines: string[];
  level: "info" | "error";
}

function fmtArg(v: unknown): string {
  if (typeof v === "string") return v;
  if (v === undefined) return "undefined";
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Run `fn` with `console.{log,info,warn,error}` temporarily intercepted, return
 * its result plus every captured line. The original console is ALWAYS restored
 * (even on throw) and still receives the call (dev-tools keep working).
 */
export function captureConsole<T>(fn: () => T): { result: T; lines: string[] } {
  const lines: string[] = [];
  const methods = ["log", "info", "warn", "error"] as const;
  const orig: Record<string, (...a: unknown[]) => void> = {};
  for (const m of methods) {
    orig[m] = console[m];
    console[m] = (...args: unknown[]) => {
      lines.push(args.map(fmtArg).join(" "));
      orig[m](...args);
    };
  }
  try {
    return { result: fn(), lines };
  } finally {
    for (const m of methods) console[m] = orig[m];
  }
}

/** Current local time as HH:MM:SS — for a log entry's timestamp. */
export function nowHms(): string {
  return new Date().toLocaleTimeString("it-IT", { hour12: false });
}
